import { Controller } from "@hotwired/stimulus";

const START_MODES = new Set([ "manual", "url", "text" ]);
const MODE_LABELS = {
  manual: "manual entry",
  url: "a recipe link",
  text: "recipe text"
};

export default class extends Controller {
  static targets = [ "modeSelect", "modeForm", "detailsHeading" ];
  static values = {
    mode: { type: String, default: "manual" },
    hasCapture: { type: Boolean, default: false },
    focusDetails: { type: Boolean, default: false }
  };

  connect() {
    this.captureStarted = () => this.lockMode();
    this.captureCompleted = () => this.unlockMode();
    this.captureFailed = () => this.unlockMode();
    this.captureReset = () => this.unlockMode();
    this.element.addEventListener("saved-recipe-capture:started", this.captureStarted);
    this.element.addEventListener("saved-recipe-capture:completed", this.captureCompleted);
    this.element.addEventListener("saved-recipe-capture:failed", this.captureFailed);
    this.element.addEventListener("saved-recipe-capture:reset", this.captureReset);

    this.modeValue = this.normalize(this.modeValue || this.modeSelectTarget.value);
    this.modeSelectTarget.value = this.modeValue;
    const initialFieldsInput = this.element.querySelector("input[name='saved_recipe_form_initial_fields']");
    this.element.dataset.savedRecipeFormInitialFields = initialFieldsInput?.value ||
      this.element.dataset.savedRecipeFormInitialFields ||
      JSON.stringify(this.recipeFieldSnapshot());
    if (initialFieldsInput && !initialFieldsInput.value) {
      initialFieldsInput.value = this.element.dataset.savedRecipeFormInitialFields;
    }
    if (this.focusDetailsValue && this.hasDetailsHeadingTarget) this.focusRecipeDetails();
  }

  disconnect() {
    this.element.removeEventListener("saved-recipe-capture:started", this.captureStarted);
    this.element.removeEventListener("saved-recipe-capture:completed", this.captureCompleted);
    this.element.removeEventListener("saved-recipe-capture:failed", this.captureFailed);
    this.element.removeEventListener("saved-recipe-capture:reset", this.captureReset);
  }

  change(event) {
    if (event.target !== this.modeSelectTarget) return;

    const mode = this.normalize(event.target.value);
    if (mode === this.modeValue) return;

    const confirmation = this.modeChangeConfirmation(mode);
    if (confirmation && !window.confirm(confirmation)) {
      this.modeSelectTarget.value = this.modeValue;
      return;
    }

    this.modeValue = mode;
    this.modeFormTarget.requestSubmit();
  }

  restartCapture(event) {
    const initialFields = this.element.dataset.savedRecipeFormInitialFields;
    const currentFields = JSON.stringify(this.recipeFieldSnapshot());
    if (!initialFields || initialFields === currentFields) return;

    const modeLabel = this.modeValue === "text" ? "recipe text" : "a recipe link";
    if (!window.confirm(`Start over with ${modeLabel}? Your edited, unsaved recipe details will be discarded and won't carry over.`)) {
      event.preventDefault();
    }
  }

  lockMode() {
    this.modeSelectTarget.disabled = true;
  }

  unlockMode() {
    this.modeSelectTarget.disabled = false;
  }

  modeChangeConfirmation(nextMode) {
    const nextLabel = MODE_LABELS[nextMode];
    const captureResult = this.element.querySelector("[data-saved-recipe-capture-result]");
    const resultHasMeaningfulDetails = captureResult?.dataset.savedRecipeCaptureMeaningfulDetails === "true";
    const entryValue = this.currentCaptureInput();
    const recipeDetails = this.hasEnteredRecipeDetails();

    if (resultHasMeaningfulDetails || (this.hasCaptureValue && recipeDetails)) {
      return "Start over with " + nextLabel + "? The unsaved recipe details from your earlier attempt will be discarded and won't carry over.";
    }

    if (entryValue) {
      const currentLabel = this.modeValue === "text" ? "recipe text" : "recipe link";
      return "Clear the " + currentLabel + " entered here and start with " + nextLabel + "?";
    }

    if (recipeDetails) {
      return "Discard the recipe details entered here and start with " + nextLabel + "?";
    }

    return null;
  }

  currentCaptureInput() {
    const inputId = this.modeValue === "text" ? "#saved_recipe_capture_text" : "#saved_recipe_capture_url";
    const input = this.element.querySelector(inputId);
    if (!input || input.disabled || input.closest("[hidden]")) return "";

    return input.value.trim();
  }

  hasEnteredRecipeDetails() {
    const form = this.element.querySelector("form[data-controller~='recipe-editor']");
    if (!form) return false;

    return Array.from(form.querySelectorAll("input:not([type='hidden']):not([type='submit']):not([type='button']), textarea, select"))
      .some((field) => {
        if (field.type === "checkbox" || field.type === "radio") return field.checked;
        return field.value.trim() !== "";
      });
  }

  recipeFieldSnapshot() {
    const form = this.element.querySelector("form[data-controller~='recipe-editor']");
    if (!form) return [];

    return Array.from(form.elements)
      .filter((field) => field.name?.startsWith("recipe[") &&
        ![ "submit", "button", "reset", "file" ].includes(field.type))
      .map((field) => [
        field.name,
        field.type === "checkbox" || field.type === "radio" ? field.checked : field.value
      ]);
  }

  focusRecipeDetails() {
    requestAnimationFrame(() => {
      if (!this.hasDetailsHeadingTarget || !this.detailsHeadingTarget.isConnected) return;

      this.detailsHeadingTarget.focus({ preventScroll: true });
      this.detailsHeadingTarget.scrollIntoView({ block: "center", behavior: "auto" });
    });
  }

  normalize(rawMode) {
    return START_MODES.has(rawMode) ? rawMode : "manual";
  }
}
